import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  CalendarDays, Plus, Search, Edit, Trash2, Clock, CheckCircle, XCircle,
  AlertTriangle, Package, MapPin, User, ChevronLeft, ChevronRight, Play
} from 'lucide-react';

const STATUSES = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'confirmada', label: 'Confirmada', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'en_curso', label: 'En Curso', color: 'bg-green-500/20 text-green-400' },
  { value: 'completada', label: 'Completada', color: 'bg-gray-500/20 text-gray-400' },
  { value: 'cancelada', label: 'Cancelada', color: 'bg-red-500/20 text-red-400' },
];

const PRIORITIES = [
  { value: 'baja', label: 'Baja', color: 'text-gray-400' },
  { value: 'normal', label: 'Normal', color: 'text-blue-400' },
  { value: 'alta', label: 'Alta', color: 'text-orange-400' },
  { value: 'urgente', label: 'Urgente', color: 'text-red-400' },
];

const emptyForm = {
  title: '', description: '', equipment_id: '', items: [],
  start_date: '', end_date: '', responsible_id: '', responsible_name: '',
  event_name: '', location_id: '', location_name: '',
  status: 'pendiente', priority: 'normal', notes: '', auto_checkout: false
};

const ReservationsPage = () => {
  const { api } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [equipment, setEquipment] = useState([]);
  const [locations, setLocations] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calData, setCalData] = useState([]);
  const [itemEquipmentId, setItemEquipmentId] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [resRes, eqRes, locRes, respRes] = await Promise.all([
        api().get('/reservations'),
        api().get('/equipment'),
        api().get('/locations'),
        api().get('/responsibles')
      ]);
      setReservations(resRes.data);
      setEquipment(eqRes.data || []);
      setLocations(locRes.data || []);
      setResponsibles(respRes.data || []);
    } catch {
      toast.error('Error cargando datos');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await api().get(`/reservations/calendar/view?month=${calMonth}&year=${calYear}`);
      setCalData(res.data.reservations || []);
    } catch { setCalData([]); }
  }, [api, calMonth, calYear]);

  useEffect(() => { if (view === 'calendar') loadCalendar(); }, [view, loadCalendar]);

  const addItem = () => {
    if (!itemEquipmentId) return;
    const eq = equipment.find(e => e.id === itemEquipmentId);
    setForm(prev => ({
      ...prev,
      equipment_id: prev.equipment_id || itemEquipmentId,
      items: [...prev.items, { equipment_id: itemEquipmentId, equipment_name: eq?.name || '', quantity: itemQuantity }]
    }));
    setItemEquipmentId('');
    setItemQuantity(1);
  };

  const removeItem = (idx) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Ingresa un título'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Selecciona fechas'); return; }
    if (form.items.length === 0) { toast.error('Agrega al menos un equipo'); return; }
    try {
      let result;
      if (editingId) {
        result = await api().put(`/reservations/${editingId}`, form);
        toast.success('Reserva actualizada');
      } else {
        const res = await api().post('/reservations', form);
        result = res.data;
        if (result.conflict) {
          const msg = result.conflicts.map(c => `${c.equipment_name}: ${c.available} disponibles de ${c.requested} solicitados`).join('\n');
          if (window.confirm(`Hay conflictos de disponibilidad:\n${msg}\n\n¿Deseas crear la reserva de todas formas?`)) {
            await api().post('/reservations', { ...form, force: true });
          } else return;
        }
        toast.success('Reserva creada');
      }
      setShowModal(false);
      setEditingId(null);
      setForm(emptyForm);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (r) => {
    setForm(r);
    setEditingId(r.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta reserva?')) return;
    try {
      await api().delete(`/reservations/${id}`);
      toast.success('Reserva eliminada');
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api().put(`/reservations/${id}`, { status: newStatus });
      toast.success('Estado actualizado');
      loadData();
    } catch { toast.error('Error al actualizar'); }
  };

  const filtered = reservations.filter(r => {
    const matchSearch = !search || r.title?.toLowerCase().includes(search.toLowerCase()) ||
      r.event_name?.toLowerCase().includes(search.toLowerCase()) || r.responsible_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status) => { const s = STATUSES.find(x => x.value === status); return s ? <Badge className={s.color}>{s.label}</Badge> : <Badge>{status}</Badge>; };

  // Calendar helpers
  const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m - 1, 1).getDay();
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const renderCalendar = () => {
    const days = getDaysInMonth(calYear, calMonth);
    const firstDay = getFirstDayOfMonth(calYear, calMonth);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`empty-${i}`} className="h-24 border border-border/20" />);
    for (let d = 1; d <= days; d++) {
      const dateStr = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const dayReservations = calData.filter(r => r.start_date?.slice(0,10) <= dateStr && r.end_date?.slice(0,10) >= dateStr);
      cells.push(
        <div key={d} className="h-24 border border-border/20 p-1 overflow-hidden">
          <span className="text-xs font-medium">{d}</span>
          {dayReservations.slice(0, 2).map((r, i) => (
            <div key={i} className="text-[10px] bg-primary/20 text-primary rounded px-1 truncate mt-0.5">{r.title}</div>
          ))}
          {dayReservations.length > 2 && <div className="text-[10px] text-muted-foreground">+{dayReservations.length - 2} más</div>}
        </div>
      );
    }
    return cells;
  };

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="h-6 w-6 text-primary" />Reservas de Equipo</h1>
          <p className="text-sm text-muted-foreground">Reserva equipos para eventos y fechas futuras</p>
        </div>
        <div className="flex gap-2">
          <Button variant={view === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setView('list')}>Lista</Button>
          <Button variant={view === 'calendar' ? 'default' : 'outline'} size="sm" onClick={() => setView('calendar')}>Calendario</Button>
          <Button onClick={() => { setForm(emptyForm); setEditingId(null); setShowModal(true); }} data-testid="new-reservation-btn">
            <Plus className="h-4 w-4 mr-2" />Nueva Reserva
          </Button>
        </div>
      </div>

      {view === 'list' ? (
        <>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="reservation-search" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground flex items-center">{filtered.length} reserva(s)</div>
          </div>

          {/* List */}
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="text-center py-12 text-muted-foreground"><CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No hay reservas</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filtered.map(r => (
                <Card key={r.id} className="hover:border-primary/30 transition-colors" data-testid={`reservation-${r.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium">{r.title}</span>
                          {getStatusBadge(r.status)}
                          {r.priority !== 'normal' && <Badge className={PRIORITIES.find(p => p.value === r.priority)?.color}>{r.priority}</Badge>}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap mt-1">
                          <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{new Date(r.start_date).toLocaleDateString()} - {new Date(r.end_date).toLocaleDateString()}</span>
                          {r.event_name && <span className="flex items-center gap-1"><Play className="h-3 w-3" />{r.event_name}</span>}
                          {r.responsible_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{r.responsible_name}</span>}
                          {r.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.location_name}</span>}
                        </div>
                        {r.items?.length > 0 && (
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {r.items.map((item, i) => (
                              <Badge key={i} variant="outline" className="text-[10px]"><Package className="h-3 w-3 mr-1" />{item.equipment_name} x{item.quantity}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {r.status === 'pendiente' && (
                          <Button variant="ghost" size="sm" className="text-blue-400" onClick={() => handleStatusChange(r.id, 'confirmada')}>Confirmar</Button>
                        )}
                        {r.status === 'confirmada' && (
                          <Button variant="ghost" size="sm" className="text-green-400" onClick={() => handleStatusChange(r.id, 'en_curso')}>Iniciar</Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(r)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Calendar View */
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={() => { if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}><ChevronLeft className="h-4 w-4" /></Button>
              <CardTitle className="text-lg">{monthNames[calMonth - 1]} {calYear}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-0">
              {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map(d => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2 border-b">{d}</div>)}
              {renderCalendar()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" />{editingId ? 'Editar' : 'Nueva'} Reserva</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Ej: Equipo para Conferencia" data-testid="reservation-title" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha Inicio *</Label>
                <Input type="date" value={form.start_date?.split('T')[0] || ''} onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} data-testid="reservation-start-date" />
              </div>
              <div className="space-y-2">
                <Label>Fecha Fin *</Label>
                <Input type="date" value={form.end_date?.split('T')[0] || ''} onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} data-testid="reservation-end-date" />
              </div>
            </div>
            {/* Equipment Items */}
            <div className="space-y-2">
              <Label>Equipos *</Label>
              <div className="flex gap-2">
                <Select value={itemEquipmentId} onValueChange={setItemEquipmentId}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Seleccionar equipo" /></SelectTrigger>
                  <SelectContent>{equipment.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min={1} value={itemQuantity} onChange={e => setItemQuantity(parseInt(e.target.value) || 1)} className="w-20" placeholder="Cant." />
                <Button variant="outline" onClick={addItem} data-testid="add-reservation-item"><Plus className="h-4 w-4" /></Button>
              </div>
              {form.items.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-muted/30 rounded px-3 py-1.5 text-sm">
                      <span><Package className="h-3 w-3 inline mr-1" />{item.equipment_name} x{item.quantity}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><XCircle className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Responsable</Label>
                <Select value={form.responsible_id} onValueChange={v => { const r = responsibles.find(x => x.id === v); setForm(prev => ({ ...prev, responsible_id: v, responsible_name: r?.name || '' })); }}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{responsibles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select value={form.location_id} onValueChange={v => { const l = locations.find(x => x.id === v); setForm(prev => ({ ...prev, location_id: v, location_name: l?.name || '' })); }}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Evento</Label>
                <Input value={form.event_name} onChange={e => setForm(prev => ({ ...prev, event_name: e.target.value }))} placeholder="Nombre del evento" />
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={form.priority} onValueChange={v => setForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Notas adicionales..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="save-reservation-btn">{editingId ? 'Actualizar' : 'Crear Reserva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReservationsPage;
